import { useQuery } from '@tanstack/react-query'
import { Navigate, useSearchParams } from 'react-router-dom'
import { Search } from 'lucide-react'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { EmptyState } from '@/components/ui/empty-state'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { ApiError, getJSON } from '@/lib/api'
import { MemberPortalPersonActivity } from './MemberPortalPersonActivity'
import { FormerBadge, PortalPersonName } from './PortalPersonName'
import type { PortalPeopleResponse, PortalPersonRow } from './portalPeople.types'

export function MemberPortalPeople({
  organizationId,
  viewer,
}: {
  organizationId: number
  viewer: string
}) {
  const [params, setParams] = useSearchParams()
  const personKey = params.get('person')
  const change = (key: string, value: string) => {
    const next = new URLSearchParams(params)
    if (value) next.set(key, value)
    else next.delete(key)
    setParams(next)
  }
  if (personKey)
    return (
      <MemberPortalPersonActivity
        key={personKey}
        organizationId={organizationId}
        viewer={viewer}
        personKey={personKey}
        onBack={() => change('person', '')}
      />
    )
  return (
    <PeopleList
      organizationId={organizationId}
      viewer={viewer}
      search={params.get('q') ?? ''}
      change={change}
    />
  )
}

function PeopleList({
  organizationId,
  viewer,
  search,
  change,
}: {
  organizationId: number
  viewer: string
  search: string
  change: (key: string, value: string) => void
}) {
  const request = new URLSearchParams()
  if (search) request.set('q', search)
  const path = `/api/v1/member_portal/organizations/${organizationId}/dashboard/people?${request}`
  const query = useQuery({
    queryKey: ['member-portal-dashboard', viewer, organizationId, 'people', path],
    queryFn: () => getJSON<PortalPeopleResponse>(path),
    retry: false,
  })
  const data = query.isError ? undefined : query.data?.people
  if (query.error instanceof ApiError && query.error.status === 404)
    return <Navigate to="/portal" replace />
  return (
    <Card className="overflow-hidden">
      <div className="border-b border-border px-4 py-3">
        <div className="flex flex-wrap items-baseline gap-1.5">
          <h2 className="text-body font-medium">People</h2>
          {data && (
            <span className="text-caption text-muted-foreground">
              · {data.available_total} people
            </span>
          )}
        </div>
        <SearchBox label="Search people" value={search} onChange={(value) => change('q', value)} />
      </div>
      {query.isPending && <EmptyState variant="quiet" title="Loading people" />}
      {query.isError && (
        <Alert variant="destructive">
          People unavailable.{' '}
          <Button variant="outline" size="sm" onClick={() => void query.refetch()}>
            Try again
          </Button>
        </Alert>
      )}
      {data && <PeopleResults data={data} change={change} />}
    </Card>
  )
}

export function SearchBox({
  label,
  placeholder = label,
  value,
  onChange,
}: {
  label: string
  placeholder?: string
  value: string
  onChange: (value: string) => void
}) {
  return (
    <div className="relative mt-3 w-56 max-w-full">
      <Search
        className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground"
        aria-hidden="true"
      />
      <Input
        className="h-9 pl-8"
        aria-label={label}
        placeholder={placeholder}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
    </div>
  )
}

function PeopleResults({
  data,
  change,
}: {
  data: PortalPeopleResponse['people']
  change: (key: string, value: string) => void
}) {
  return (
    <>
      {data.rows.length ? (
        <PeopleTable rows={data.rows} onOpen={(key) => change('person', key)} />
      ) : (
        <EmptyState
          variant="quiet"
          title={data.available_total ? 'No people match your search' : 'No people yet'}
          action={
            data.available_total ? (
              <Button variant="outline" onClick={() => change('q', '')}>
                Clear search
              </Button>
            ) : undefined
          }
        />
      )}
      <div className="border-t border-border px-4 py-3 text-caption text-muted-foreground">
        {data.total ? `Showing 1–${data.total} of ${data.total} people` : 'Showing 0 of 0 people'}
      </div>
    </>
  )
}

function PeopleTable({ rows, onOpen }: { rows: PortalPersonRow[]; onOpen: (key: string) => void }) {
  return (
    <Table aria-label="People" className="[&_th]:whitespace-nowrap">
      <TableHeader>
        <TableRow>
          <TableHead>Person</TableHead>
          <TableHead>Membership level</TableHead>
          <TableHead className="text-right">Groups</TableHead>
          <TableHead className="text-right">Event registrations</TableHead>
          <TableHead className="text-right">Courses completed</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row) => (
          <TableRow
            key={row.key}
            className="cursor-pointer"
            onClick={(event) => {
              if ((event.target as Element).closest('button')) return
              onOpen(row.key)
            }}
          >
            <TableCell className="whitespace-nowrap">
              <Button
                variant="link"
                size="inline"
                className="font-medium"
                onClick={() => onOpen(row.key)}
              >
                <PortalPersonName name={row.name} status={row.person_status} badge={false} />
              </Button>
              {row.person_status === 'former' && row.name !== null && <FormerBadge />}
            </TableCell>
            <TableCell>{row.membership_level ?? '—'}</TableCell>
            <TableCell className="text-right tabular-nums">{row.groups_count}</TableCell>
            <TableCell className="text-right tabular-nums">{row.event_registrations}</TableCell>
            <TableCell className="text-right tabular-nums">{row.courses_completed}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}
